import { Button, Grid, MenuItem, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Field, Form, Formik, type FormikProps } from "formik";
import { TextField } from "formik-mui";
import { findIndex } from "lodash-es";
import type React from "react";
import { type TFunction, useTranslation } from "react-i18next";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { DecimalScaleTextField } from "src/components/form-components/DecimalScaleTextField";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { adornments } from "src/config/adornments";
import { segment as decimalScale } from "src/config/decimal-scales";
import { useSegmentSchema } from "src/schemas/segment.schema";
import type { APIError, Segment, Timefactor, TimefactorType } from "src/types";

export type SegmentFormData = Omit<
	Segment,
	"segmentId" | "lineString_frontendSrid"
>;

interface Props {
	title?: string;
	readOnly?: boolean;
	data: SegmentFormData;
	timefactorType: TimefactorType;
	vehicleTypes: string[];
	roadTypes: string[];
	timefactors: Timefactor[];
	barrierHeights: number[];
	onClose: () => void;
	onSubmit: (values: SegmentFormData) => void;
	onDelete?: () => void;
	isLoading: boolean;
	errors?: APIError[] | null;
	formRef?: React.MutableRefObject<FormikProps<SegmentFormData> | null>;
	isBaselineScenario?: boolean;
}

export function SegmentForm(props: Props) {
	const { t } = useTranslation();
	const { isBaselineScenario = false } = props;
	const schema = useSegmentSchema();

	const initialValues: SegmentFormData = {
		roadType: props.data.roadType,
		height_m: props.data.height_m,
		speed_km_h: props.data.speed_km_h,
		vehicleCounts: props.data.vehicleCounts,
		ifdmTimeFactor: props.data.ifdmTimeFactor,
		barrierHeight_m: props.data.barrierHeight_m,
	};

	const filteredTimefactors = props.timefactors.filter(
		(timefactor) => timefactor.type === "ROAD",
	);

	return render();

	function render() {
		return (
			<Formik
				innerRef={props.formRef}
				initialValues={initialValues}
				validationSchema={schema}
				onSubmit={async (values: SegmentFormData) => {
					
					return props.onSubmit(values);
				}}
				enableReinitialize
			>
				{({ isSubmitting }) => (
					<Styles>
						<Grid container spacing={3}>
							{props.title && (
								<Grid item xs={12}>
									<Typography variant="h2">{props.title}</Typography>
								</Grid>
							)}
							<Grid item xs={12}>
								<Typography variant="body1">
									{t("label.trafficCount")}
								</Typography>
								<Typography variant="body2" color="textSecondary">
									{t("label.timefactorType")}:{" "}
									{t(`label.timefactorTypes.${props.timefactorType}`)}
								</Typography>
							</Grid>
							{props.vehicleTypes.map((vehicleType) => {
								const vehicleTypeIndex = findIndex(props.data.vehicleCounts, {
									vehicleType,
								});
								return (
									<Grid
										item
										xs={12}
										key={["vehicleCounts", vehicleType].join()}
									>
										<Field
											component={DecimalScaleTextField}
											decimalScale={decimalScale.vehicleCounts}
											name={`vehicleCounts[${vehicleTypeIndex}].count`}
											label={t(`label.vehicleType.${vehicleType}`)}
											type="number"
											variant="outlined"
											fullWidth
											disabled={props.readOnly}
											inputProps={{ step: "any" }}
											helperText={
												!isBaselineScenario
													? oldVehicleCountsValueHelperText(
															props.data,
															vehicleTypeIndex,
															t,
														)
													: ""
											}
										/>
									</Grid>
								);
							})}
							<Grid item xs={12}>
								<Typography>{t("label.segmentProperties")}</Typography>
							</Grid>
							<Grid item xs={12}>
								<Field
									component={DecimalScaleTextField}
									decimalScale={decimalScale.speed_km_h}
									name="speed_km_h"
									label={t("label.speedLimit")}
									type="number"
									variant="outlined"
									InputProps={adornments(t).kmPerHour}
									fullWidth
									disabled={props.readOnly}
									inputProps={{ step: "any" }}
									helperText={
										!isBaselineScenario
											? oldValueHelperText(props.data, "speedOld_km_h", t)
											: ""
									}
								/>
							</Grid>
							<Grid item xs={12}>
								<Field
									component={DecimalScaleTextField}
									decimalScale={decimalScale.height_m}
									name="height_m"
									label={t("label.height")}
									type="number"
									variant="outlined"
									InputProps={adornments(t).meter}
									fullWidth
									disabled={props.readOnly}
									inputProps={{ step: "any" }}
									helperText={
										!isBaselineScenario
											? oldValueHelperText(props.data, "heightOld_m", t)
											: ""
									}
								/>
							</Grid>
							<Grid item xs={12}>
								<Field
									component={TextField}
									name="roadType"
									label={t("label.roadType")}
									type="text"
									select={true}
									variant="outlined"
									fullWidth
									disabled={props.readOnly}
									helperText={
										!isBaselineScenario
											? oldValueHelperText(props.data, "roadTypeOld", t)
											: ""
									}
								>
									<MenuItem value={""}>
										<em>{t("label.roadType_placeholder")}</em>
									</MenuItem>
									{props.roadTypes.map((roadType) => (
										<MenuItem key={roadType} value={roadType}>
											{t(`label.roadTypeDict.${roadType}`)}
										</MenuItem>
									))}
								</Field>
							</Grid>
							<Grid item xs={12}>
								<Field
									component={TextField}
									name="ifdmTimeFactor"
									label={t("label.timefactor")}
									type="text"
									select={true}
									variant="outlined"
									fullWidth
									disabled={props.readOnly}
									helperText={
										!isBaselineScenario
											? oldTimefactorValueHelperText(
													props.data,
													filteredTimefactors,
													t,
												)
											: ""
									}
								>
									<MenuItem value={""}>
										<em>{t("label.timefactor_placeholder")}</em>
									</MenuItem>
									{filteredTimefactors.map((timefactor) => (
										<MenuItem key={timefactor.id} value={timefactor.nameKey}>
											{timefactor.nameTranslation}
										</MenuItem>
									))}
								</Field>
							</Grid>
							<Grid item xs={12}>
								<Field
									component={TextField}
									name="barrierHeight_m"
									label={t("label.barrierHeight")}
									type="text"
									select={true}
									className="select-with-adornment"
									variant="outlined"
									fullWidth
									disabled={props.readOnly}
									helperText={
										!isBaselineScenario
											? oldValueHelperText(props.data, "barrierHeightOld_m", t)
											: ""
									}
									InputProps={adornments(t).meter}
								>
									<MenuItem value={""}>
										<em>{t("label.barrierHeight_placeholder")}</em>
									</MenuItem>
									{props.barrierHeights.map((barrierHeight) => (
										<MenuItem key={barrierHeight} value={barrierHeight}>
											{barrierHeight}
										</MenuItem>
									))}
								</Field>
							</Grid>

							{props.errors && (
								<Grid item xs={12}>
									<ServerErrors data={props.errors} />
								</Grid>
							)}
							{!props.readOnly && (
								<>
									<ButtonGrid
										container
										item
										xs={12}
										sx={{ justifyContent: "flex-end" }}
									>
										<Button variant="text" onClick={props.onClose}>
											{t("label.cancel")}
										</Button>
										<ButtonLoading
											isLoading={isSubmitting || props.isLoading}
											type="submit"
											color="primary"
											variant="contained"
										>
											{t("label.save")}
										</ButtonLoading>
									</ButtonGrid>

									<ButtonGrid
										container
										item
										xs={12}
										sx={{ justifyContent: "flex-end" }}
									>
										{props.onDelete && (
											<DeleteButton variant="text" onClick={props.onDelete}>
												{t("label.delete")}
											</DeleteButton>
										)}
									</ButtonGrid>
								</>
							)}

							{props.readOnly && (
								<ButtonGrid
									container
									item
									xs={12}
									sx={{ justifyContent: "flex-end" }}
								>
									<Button variant="text" onClick={props.onClose}>
										{t("label.close")}
									</Button>
								</ButtonGrid>
							)}
						</Grid>
					</Styles>
				)}
			</Formik>
		);
	}
}

const DeleteButton = styled(Button)`
  color: ${({ theme }) => theme.palette.error.main};
`;

/**
 * Utils
 */
function oldTimefactorValueHelperText(
	data: SegmentFormData,
	timefactors: Timefactor[],
	t: TFunction,
) {
	if (!data.ifdmTimeFactorOld) return "";
	const timefactor = timefactors.find(
		(timefactor) => timefactor.nameKey === data.ifdmTimeFactorOld,
	);
	return t("label.initialValue", { value: timefactor?.nameTranslation });
}

function oldValueHelperText<T = SegmentFormData>(
	data: T,
	property: keyof T,
	t: TFunction,
) {
	if (data[property] === undefined || data[property] === null) return "";
	else if (property === "roadTypeOld") {
		return t("label.initialValue", {
			value: t(`label.roadTypeDict.${data[property]}`),
		});
	} else {
		return t("label.initialValue", { value: data[property] });
	}
}

function oldVehicleCountsValueHelperText(
	data: SegmentFormData,
	vehicleTypeIndex: number,
	t: TFunction,
) {
	if (!data.vehicleCountsOld) return "";
	else if (data.vehicleCountsOld.length === 0) return "";
	else {
		return t("label.initialValue", {
			value: data.vehicleCountsOld[vehicleTypeIndex].count,
		});
	}
}

const Styles = styled(Form)`
  .select-with-adornment .MuiInputAdornment-root {
    margin-right: 16px;
  }
`;
